/**
 * MagicBI Design System - Spacing Tokens
 * 
 * Consistent spacing system based on 4px grid
 * with semantic naming for layout and components.
 */

// Base spacing unit (4px)
const baseUnit = 4;

// Spacing scale (in pixels, converted to rem)
export const spacing = {
  0: '0',
  /** Half step — Figma AI summary card header↔body gap is 2px (node 4306:346315). */
  0.5: `${baseUnit * 0.5}px`,  // 2px
  1: `${baseUnit * 1}px`,      // 4px
  /** Half step — Figma Chart Studio format field label↔control gap is 6px (node 6409:556192). */
  1.5: `${baseUnit * 1.5}px`,  // 6px
  2: `${baseUnit * 2}px`,      // 8px
  /** Half step — Figma `_input-small` vertical padding is 10px (node 5326:88533). */
  2.5: `${baseUnit * 2.5}px`,  // 10px
  3: `${baseUnit * 3}px`,      // 12px
  /** Half step: 14px (control-band padding, metric tile padding). */
  3.5: '0.875rem',          // 14px
  4: `${baseUnit * 4}px`,      // 16px
  /** Half step: 18px (header band row gap). */
  4.5: '1.125rem',          // 18px
  5: `${baseUnit * 5}px`,      // 20px
  6: `${baseUnit * 6}px`,      // 24px
  7: `${baseUnit * 7}px`,      // 28px
  /** 30px (setting value pill height). */
  7.5: '1.875rem',          // 30px
  8: `${baseUnit * 8}px`,      // 32px
  9: `${baseUnit * 9}px`,      // 36px
  10: `${baseUnit * 10}px`,    // 40px
  11: `${baseUnit * 11}px`,    // 44px
  12: `${baseUnit * 12}px`,    // 48px
  /** 52px (stage icon tile, value pill width). */
  13: '3.25rem',            // 52px
  14: `${baseUnit * 14}px`,    // 56px
  16: `${baseUnit * 16}px`,    // 64px
  20: `${baseUnit * 20}px`,    // 80px
  24: `${baseUnit * 24}px`,    // 96px
  28: `${baseUnit * 28}px`,    // 112px
  32: `${baseUnit * 32}px`,    // 128px
  36: `${baseUnit * 36}px`,    // 144px
  40: `${baseUnit * 40}px`,    // 160px
  44: `${baseUnit * 44}px`,    // 176px
  48: `${baseUnit * 48}px`,    // 192px
  52: `${baseUnit * 52}px`,    // 208px
  56: `${baseUnit * 56}px`,    // 224px
  60: `${baseUnit * 60}px`,    // 240px
  64: `${baseUnit * 64}px`,    // 256px
  72: `${baseUnit * 72}px`,    // 288px
  76: '4.75rem',                // 76px (ExtensionStack)
  80: `${baseUnit * 80}px`,    // 320px
  96: `${baseUnit * 96}px`,    // 384px
} as const;

// Semantic spacing tokens
export const semanticSpacing = {
  // Insets (padding)
  inset: {
    none: spacing[0],
    xs: spacing[1],      // 4px
    sm: spacing[2],      // 8px
    md: spacing[4],      // 16px
    lg: spacing[6],      // 24px
    xl: spacing[8],      // 32px
    '2xl': spacing[12],  // 48px
    '3xl': spacing[16],  // 64px
  },

  // Stack (vertical spacing)
  stack: {
    none: spacing[0],
    xs: spacing[1],      // 4px
    sm: spacing[2],      // 8px
    md: spacing[4],      // 16px
    lg: spacing[6],      // 24px
    xl: spacing[8],      // 32px
    '2xl': spacing[12],  // 48px
    '3xl': spacing[16],  // 64px
    '4xl': spacing[20],  // 80px
    '5xl': spacing[24],  // 96px
  },

  // Inline (horizontal spacing)
  inline: {
    none: spacing[0],
    xs: spacing[1],      // 4px
    sm: spacing[2],      // 8px
    md: spacing[4],      // 16px
    lg: spacing[6],      // 24px
    xl: spacing[8],      // 32px
    '2xl': spacing[12],  // 48px
    '3xl': spacing[16],  // 64px
  },

  // Squish (different horizontal/vertical padding)
  squish: {
    sm: {
      horizontal: spacing[3],  // 12px
      vertical: spacing[2],    // 8px
    },
    md: {
      horizontal: spacing[4],  // 16px
      vertical: spacing[3],    // 12px
    },
    lg: {
      horizontal: spacing[6],  // 24px
      vertical: spacing[4],    // 16px
    },
  },
} as const;

// Component-specific spacing
export const componentSpacing = {
  // Button spacing
  button: {
    paddingX: {
      sm: spacing[3],    // 12px
      md: spacing[4],    // 16px
      lg: spacing[6],    // 24px
    },
    paddingY: {
      sm: spacing[2],    // 8px
      md: spacing[3],    // 12px
      lg: spacing[4],    // 16px
    },
    gap: spacing[2],     // 8px (icon-text gap)
  },

  // Input spacing
  input: {
    paddingX: spacing[3],    // 12px
    paddingY: spacing[3],    // 12px
    gap: spacing[2],         // 8px (icon gap)
  },

  // Card spacing
  card: {
    padding: {
      sm: spacing[4],    // 16px
      md: spacing[6],    // 24px
      lg: spacing[8],    // 32px
    },
    gap: spacing[4],     // 16px (content gap)
  },

  // Modal spacing
  modal: {
    padding: spacing[6],     // 24px
    gap: spacing[6],         // 24px
    margin: spacing[4],      // 16px
  },

  // Navigation spacing
  nav: {
    padding: spacing[4],     // 16px
    itemGap: spacing[1],     // 4px
    sectionGap: spacing[6],  // 24px
  },

  // List spacing
  list: {
    gap: spacing[2],         // 8px
    padding: spacing[4],     // 16px
  },

  // Form spacing
  form: {
    fieldGap: spacing[4],    // 16px
    sectionGap: spacing[8],  // 32px
    labelGap: spacing[1],    // 4px
  },
} as const;

// Layout spacing (larger values for page layout)
export const layoutSpacing = {
  // Container spacing
  container: {
    paddingX: {
      mobile: spacing[4],    // 16px
      tablet: spacing[6],    // 24px
      desktop: spacing[8],   // 32px
    },
    paddingY: {
      mobile: spacing[6],    // 24px
      tablet: spacing[8],    // 32px
      desktop: spacing[12],  // 48px
    },
  },

  // Section spacing
  section: {
    gap: {
      sm: spacing[16],       // 64px
      md: spacing[24],       // 96px
      lg: spacing[32],       // 128px
    },
  },

  // Grid spacing
  grid: {
    gap: {
      sm: spacing[4],        // 16px
      md: spacing[6],        // 24px
      lg: spacing[8],        // 32px
    },
    gutter: {
      mobile: spacing[4],    // 16px
      tablet: spacing[6],    // 24px
      desktop: spacing[8],   // 32px
    },
  },
} as const;

// Type definitions
export type Spacing = typeof spacing;
export type SpacingKey = keyof Spacing;
export type SemanticSpacing = typeof semanticSpacing;
export type ComponentSpacing = typeof componentSpacing;
export type LayoutSpacing = typeof layoutSpacing;

// Utility functions
export const getSpacing = (value: SpacingKey) => spacing[value];

export const getSemanticSpacing = <T extends keyof SemanticSpacing>(
  category: T,
  size: keyof SemanticSpacing[T]
) => {
  return semanticSpacing[category][size];
};

// CSS helper for styled-components
export const spacingMixin = {
  inset: (size: keyof typeof semanticSpacing.inset) => `
    padding: ${semanticSpacing.inset[size]};
  `,
  
  insetX: (size: keyof typeof semanticSpacing.inset) => `
    padding-left: ${semanticSpacing.inset[size]};
    padding-right: ${semanticSpacing.inset[size]};
  `,
  
  insetY: (size: keyof typeof semanticSpacing.inset) => `
    padding-top: ${semanticSpacing.inset[size]};
    padding-bottom: ${semanticSpacing.inset[size]};
  `,
  
  stack: (size: keyof typeof semanticSpacing.stack) => `
    margin-bottom: ${semanticSpacing.stack[size]};
  `,
  
  inline: (size: keyof typeof semanticSpacing.inline) => `
    margin-right: ${semanticSpacing.inline[size]};
  `,
  
  squish: (size: keyof typeof semanticSpacing.squish) => {
    const squishValue = semanticSpacing.squish[size];
    return `
      padding: ${squishValue.vertical} ${squishValue.horizontal};
    `;
  },
};
